import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API_BASE, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import { Share2, X, Copy, Download, ChevronRight, Loader2 } from "lucide-react";

const renderUrl = (cardId, locale, size = "square", showDetails = false) =>
  `${API_BASE}/share-cards/render?card_id=${encodeURIComponent(cardId)}&locale=${locale}&size=${size}${showDetails ? "&show_details=true" : ""}`;

function relTime(iso, lang) {
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    const de = lang === "de";
    if (diff < 60) return de ? "gerade eben" : "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch { return ""; }
}

export default function ShareCards() {
  const { t, lang } = useI18n();
  usePageMeta(t("share_cards_title"));
  const locale = lang === "de" ? "de" : lang === "es" ? "es" : lang === "fr" ? "fr" : "en";

  const [tab, setTab] = useState("all");
  const [filter, setFilter] = useState("all");
  const [cards, setCards] = useState([]);
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cat, hist] = await Promise.all([
        api.get("/share-cards/catalogue").then((r) => r.data).catch(() => ({ cards: [] })),
        api.get("/share-cards/my-shared").then((r) => r.data).catch(() => ({ grouped: [] })),
      ]);
      setCards((cat?.cards || []).filter((c) => c.copy_ready));
      setShared(hist?.grouped || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => (filter === "all" ? cards : cards.filter((c) => c.category === filter)), [cards, filter]);
  const sentRows = useMemo(() => {
    const byId = new Map(cards.map((c) => [c.id, c]));
    return shared.map((g) => ({ ...g, meta: byId.get(g.card_id) })).filter((r) => r.meta);
  }, [shared, cards]);

  const copyFor = (c) => (c?.copy?.[locale] || c?.copy?.en || {});

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="share-cards-hub">
      <div>
        <h1 className="font-display text-3xl text-foreground">{t("share_cards_title")}</h1>
      </div>

      <div className="flex gap-1 rounded-xl bg-secondary p-1">
        {["all", "sent"].map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} data-testid={`sc-tab-${tb}`}
            className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${tab === tb ? "bg-background text-primary shadow-sm" : "text-muted-foreground"}`}>
            {tb === "all" ? `${t("sc_tab_all")} (${visible.length})` : `${t("sc_tab_sent")} (${sentRows.length})`}
          </button>
        ))}
      </div>

      {tab === "all" ? (
        <div className="flex gap-2">
          {["all", "client", "coach"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`sc-filter-${f}`}
              className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${filter === f ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
              {t(`sc_filter_${f}`)}
            </button>
          ))}
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : tab === "all" ? (
        visible.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">{t("sc_empty_filter")}</p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {visible.map((c) => {
              const copy = copyFor(c);
              return (
                <button key={c.id} onClick={() => setActive(c)} data-testid={`sc-card-${c.id}`} className="text-left">
                  <div className="aspect-square overflow-hidden rounded-xl border border-border bg-secondary">
                    <img src={renderUrl(c.id, locale, "thumb")} alt={copy.head || c.id} className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  <p className="mt-2 line-clamp-2 font-display text-sm text-foreground">{copy.head || c.id}</p>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{copy.kicker || (c.category === "coach" ? "Coach" : "Client")}</p>
                </button>
              );
            })}
          </div>
        )
      ) : (
        sentRows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-14 text-center">
            <Share2 className="h-10 w-10 text-muted-foreground/50" />
            <p className="font-display text-base text-foreground">{t("sc_empty_sent_head")}</p>
            <p className="max-w-xs text-sm text-muted-foreground">{t("sc_empty_sent_body")}</p>
            <button onClick={() => setTab("all")} data-testid="sc-browse" className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">{t("sc_browse")}</button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {sentRows.map((r) => {
              const copy = copyFor(r.meta);
              return (
                <button key={r.card_id} onClick={() => setActive(r.meta)} data-testid={`sc-sent-${r.card_id}`}
                  className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-2.5 text-left hover:bg-accent">
                  <img src={renderUrl(r.card_id, locale, "thumb")} alt="" className="h-16 w-16 rounded-lg object-cover" loading="lazy" />
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 font-display text-sm text-foreground">{copy.head || r.card_id}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{r.count}× · {relTime(r.last_shared_at, lang)}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        )
      )}

      {active ? <ShareModal card={active} locale={locale} onClose={() => { setActive(null); load(); }} /> : null}
    </div>
  );
}

function ShareModal({ card, locale, onClose }) {
  const { t } = useI18n();
  const [details, setDetails] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  useEffect(() => {
    api.get(`/share-cards/share-link?card_id=${encodeURIComponent(card.id)}`)
      .then((r) => setShareUrl(r.data?.share_url || ""))
      .catch(() => setShareUrl(`https://my-within.com/s/${card.id}`));
  }, [card.id]);

  const logShared = () => { api.post(`/share-cards/${encodeURIComponent(card.id)}/shared`).catch(() => {}); };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      logShared();
      toast.success(t("sc_copied"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const nativeShare = async () => {
    try {
      if (navigator.share) { await navigator.share({ url: shareUrl, title: "My Within" }); logShared(); }
      else { await copyLink(); }
    } catch { /* cancelled */ }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center" onClick={onClose} data-testid="sc-modal">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-background sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="font-display text-base text-foreground">{t("sc_share_moment")} 🌱</h2>
          <button onClick={onClose} data-testid="sc-modal-close" className="rounded-full p-1 hover:bg-accent"><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>
        <div className="space-y-4 p-4">
          <img src={renderUrl(card.id, locale, "square", details)} alt="" className="mx-auto w-full max-w-[320px] rounded-xl border border-border" />

          <button onClick={() => setDetails((v) => !v)} data-testid="sc-details-toggle" className="flex w-full items-start gap-3 rounded-xl bg-secondary p-3 text-left">
            <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${details ? "border-primary bg-primary text-primary-foreground" : "border-primary"}`}>{details ? "✓" : ""}</span>
            <span className="text-sm font-medium text-foreground">{t("sc_share_details")}</span>
          </button>

          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("sc_your_link")}</p>
            <div className="flex items-center gap-2">
              <input readOnly value={shareUrl} className="min-w-0 flex-1 truncate rounded-lg border border-border bg-secondary px-3 py-2 font-mono text-xs text-foreground" data-testid="sc-share-url" />
              <button onClick={copyLink} data-testid="sc-copy-link" className="flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground"><Copy className="h-3.5 w-3.5" /> {t("sc_copy")}</button>
            </div>
          </div>

          <div className="space-y-2">
            {[t("sc_step1"), t("sc_step2"), t("sc_step3")].map((st, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">{i + 1}</span>
                <span className="text-sm text-foreground">{st}</span>
              </div>
            ))}
            <p className="pt-1 text-xs italic text-muted-foreground">{t("sc_steps_note")}</p>
          </div>

          <div className="flex gap-2">
            <a href={renderUrl(card.id, locale, "square", details)} download={`${card.id}.png`} target="_blank" rel="noopener noreferrer" onClick={logShared} data-testid="sc-download"
              className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-primary/50 py-3 text-sm font-semibold text-primary"><Download className="h-4 w-4" /> {t("sc_download")}</a>
            <button onClick={nativeShare} data-testid="sc-native-share" className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground"><Share2 className="h-4 w-4" /> {t("sc_share")}</button>
          </div>

          <p className="text-[11px] leading-relaxed text-muted-foreground">{t("sc_fine")}</p>
        </div>
      </div>
    </div>
  );
}
